import React, { useCallback, useEffect, useRef, useState } from "react";

// Util
import { Link } from "react-router-dom";
import AccessControl from "../ButtonComponents/AccessControl";

// MUI
import {
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

// MUI Icons

import FindReplaceIcon from "@mui/icons-material/FindReplace";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import UpdateIcon from "@mui/icons-material/Update";
import ChecklistIcon from "@mui/icons-material/Checklist";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  CheckCircleOutline,
  InsertDriveFile,
  PlayCircleOutline,
  Restore,
  Error,
  PublishOutlined,
  SaveOutlined,
  Send,
  Schedule,
  Security,
  CodeOutlined,
} from "@mui/icons-material";
import { CanvasHeaderButton } from "./CanvasStyledComponents";
import { tooltips } from "../../../../utils/CanvasUtil";
import CompareArrows from "@mui/icons-material/CompareArrows";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import FileLibraryModal from "../../../FileLibrary/FileLibraryModal";
import ProjectLogModal from "../../../pages/ProjectLog/ProjectLogModal";
import WorkflowSchedulesModal from "../../../pages/WorkflowSchedules/WorkflowSchedulesModal";
import ErrorHandlingModal from "../../../pages/ErrorHandling/ErrorHandlingModal";
import ManagedRunPolicyModal from "../../../pages/ManagedRunPolicy/ManagedRunPolicyModal";
import useProjectStore from "../../../ProjectCanvas/useProjectStore";
import { hasManagedRunsAccessForClient } from "../../../../constants/featureFlags";
// Minimum container widths needed to keep each action visible without
// clipping. More remains visible; every other action moves into its menu.
const COLLAPSE_THRESHOLDS = {
  publish: 220,
  save: 320,
  access: 520,
  run: 620,
  sessions: 760,
  files: 870,
  code: 980,
  managedRunStatus: 1100,
};

function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(Infinity);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

export default function MainCanvasButtons(props) {
  const {
    production,
    pauseSave,
    saving,
    isCanvasLocked,
    firePublish,
    updateState,
    anchorEl,
    saveDraft,
    saveDraftVersion,
    handleTest,
    publishedVersion,
    handleRenamePlaceholders,
    aggregate,
    error,
    handleAccess,
    rerenderBadge,
    setLoadingDialog,
    checkUnpublishedChanges,
    onDescriptionClick,
    codeViewOpen,
    onToggleCodeView,
  } = props;

  const [containerRef, containerWidth] = useContainerWidth();
  const clientId = useProjectStore((s) => s.clientId);
  const hasManagedRunsAccess = hasManagedRunsAccessForClient(clientId);

  const [fileLibraryOpen, setFileLibraryOpen] = useState(false);
  const [projectLogOpen, setProjectLogOpen] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [errorHandlingOpen, setErrorHandlingOpen] = useState(false);
  const [managedRunPolicyOpen, setManagedRunPolicyOpen] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const managedRunsEnabled =
    aggregate?.extras?.managedRunPolicy?.enabled === true;
  const hasAccessControl = Boolean(
    aggregate?.project_id && aggregate?.components
  );

  const saveDisabled = production || pauseSave || saving || isCanvasLocked;
  const responsiveActions = [
    {
      key: "publish",
      label: "Publish",
      icon: <Send />,
      tooltip: tooltips.publish,
      threshold: COLLAPSE_THRESHOLDS.publish,
      disabled: saveDisabled,
      onClick: firePublish,
      className: "canvas-button-disabled",
      suffix: <div className="atom-dot" />,
    },
    {
      key: "save",
      label: "Save",
      icon: <SaveOutlined />,
      tooltip: tooltips.save,
      threshold: COLLAPSE_THRESHOLDS.save,
      disabled: saveDisabled,
      onClick: (event) => updateState({ anchorEl: event.target }),
      className: "canvas-button-disabled",
      overflowItems: [
        {
          key: "save",
          label: "Save",
          icon: <SaveOutlined />,
          disabled: saveDisabled,
          onClick: saveDraft,
        },
        {
          key: "save-version",
          label: "Save as version",
          icon: <BookmarkIcon />,
          disabled: saveDisabled,
          onClick: saveDraftVersion,
        },
      ],
    },
    {
      key: "code",
      label: codeViewOpen ? "Hide code" : "Show code",
      icon: <CodeOutlined />,
      inlineLabel: "Code",
      tooltip: codeViewOpen ? "Hide workflow code" : "Show workflow code",
      threshold: COLLAPSE_THRESHOLDS.code,
      disabled: false,
      onClick: onToggleCodeView,
      variant: codeViewOpen ? "contained" : "outlined",
      ariaPressed: codeViewOpen,
    },
    {
      key: "run",
      label: "Run",
      icon: <PlayCircleOutline />,
      tooltip: tooltips.start,
      threshold: COLLAPSE_THRESHOLDS.run,
      disabled: production || isCanvasLocked || publishedVersion === "None",
      onClick: () => {
        setLoadingDialog(true);
        checkUnpublishedChanges(true);
      },
    },
    {
      key: "sessions",
      label: "Sessions",
      icon: <ChecklistIcon />,
      tooltip: tooltips.logs,
      threshold: COLLAPSE_THRESHOLDS.sessions,
      disabled: production || isCanvasLocked,
      onClick: () => setProjectLogOpen(true),
    },
    {
      key: "files",
      label: "Files",
      icon: <InsertDriveFile />,
      tooltip: tooltips.files,
      threshold: COLLAPSE_THRESHOLDS.files,
      disabled: production || isCanvasLocked,
      onClick: () => setFileLibraryOpen(true),
    },
    {
      key: "access",
      label: "Access",
      icon: <Security />,
      threshold: COLLAPSE_THRESHOLDS.access,
      visible: hasAccessControl,
      disabled: isCanvasLocked,
      onClick: handleAccess,
      renderInline: () => (
        <AccessControl
          handleAccess={handleAccess}
          components={aggregate.components}
          projectId={aggregate.project_id}
          rerenderBadge={rerenderBadge}
          tooltips={tooltips}
          isCanvasLocked={isCanvasLocked}
        />
      ),
    },
    {
      key: "managed-run-status",
      label: "Managed run: On",
      icon: <Security />,
      threshold: COLLAPSE_THRESHOLDS.managedRunStatus,
      visible: managedRunsEnabled,
      disabled: true,
      hideInlineIcon: true,
    },
  ].map((action) => ({
    ...action,
    visible: action.visible !== false,
    inline: containerWidth > action.threshold,
  }));
  const overflowItems = responsiveActions
    .filter((action) => action.visible && !action.inline)
    .flatMap((action) => action.overflowItems ?? [action]);
  return (
    <Grid
      ref={containerRef}
      container
      alignItems={"center"}
      spacing={1}
      wrap="nowrap"
      sx={{ width: "100%", margin: 0 }}
    >
      {responsiveActions
        .filter((action) => action.visible && action.inline)
        .map((action) => (
          <Grid item key={action.key}>
            {action.renderInline ? (
              action.renderInline()
            ) : (
              <Tooltip title={action.tooltip ?? action.label}>
                <span>
                  <CanvasHeaderButton
                    disabled={action.disabled}
                    variant={action.variant ?? "outlined"}
                    onClick={action.onClick}
                    className={action.className}
                    startIcon={action.hideInlineIcon ? undefined : action.icon}
                    aria-pressed={action.ariaPressed}
                  >
                    {action.inlineLabel ?? action.label}
                    {action.suffix}
                  </CanvasHeaderButton>
                </span>
              </Tooltip>
            )}
          </Grid>
        ))}

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => updateState({ anchorEl: null })}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
      >
        <MenuItem
          onClick={() => {
            saveDraft();
            updateState({ anchorEl: null });
          }}
        >
          <SaveOutlined sx={{ mr: 1 }} /> Save
        </MenuItem>
        <MenuItem
          onClick={() => {
            saveDraftVersion();
            updateState({ anchorEl: null });
          }}
        >
          <BookmarkIcon sx={{ mr: 1 }} /> Save as version
        </MenuItem>
      </Menu>

      <ProjectLogModal
        open={projectLogOpen}
        onClose={() => setProjectLogOpen(false)}
        projectId={aggregate.project_id}
        projectName={aggregate.project_name}
      />
      <FileLibraryModal
        open={fileLibraryOpen}
        onClose={() => setFileLibraryOpen(false)}
        projectId={aggregate.project_id}
      />

      {/* --- Always visible: More menu --- */}
      <Tooltip title="More options">
        <Grid item>
          <CanvasHeaderButton
            variant={"outlined"}
            startIcon={<MoreVertIcon />}
            onClick={(event) => setMoreMenuAnchor(event.target)}
          >
            More
          </CanvasHeaderButton>
        </Grid>
      </Tooltip>
      <Menu
        id="more-options-menu"
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        MenuListProps={{ "aria-labelledby": "more-options-button" }}
      >
        {overflowItems.map((item) => (
          <MenuItem
            key={item.key}
            onClick={(event) => {
              setMoreMenuAnchor(null);
              item.onClick?.(event);
            }}
            disabled={item.disabled}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
        {overflowItems.length > 0 && <Divider />}

        <MenuItem
          onClick={() => {
            onDescriptionClick?.();
            setMoreMenuAnchor(null);
          }}
          disabled={isCanvasLocked}
        >
          <DescriptionIcon sx={{ mr: 1 }} /> Description
        </MenuItem>
        <Link
          className={"canvasLink"}
          to={`/project/version_history/${aggregate.project_id}`}
        >
          <MenuItem
            onClick={() => {
              setMoreMenuAnchor(null);
            }}
            disabled={production || isCanvasLocked}
          >
            <UpdateIcon sx={{ mr: 1 }} /> Version History
          </MenuItem>
        </Link>
        <Link
          className={"canvasLink"}
          to={`/project/workflow_schema/${aggregate.project_id}`}
        >
          <MenuItem
            onClick={() => {
              setMoreMenuAnchor(null);
            }}
            disabled={production || isCanvasLocked}
          >
            <CompareArrows sx={{ mr: 1 }} /> Compare Versions
          </MenuItem>
        </Link>
        <MenuItem
          onClick={() => {
            setSchedulesOpen(true);
            setMoreMenuAnchor(null);
          }}
          disabled={isCanvasLocked}
        >
          <Schedule sx={{ mr: 1 }} /> Schedules
        </MenuItem>
        <MenuItem
          onClick={() => {
            setErrorHandlingOpen(true);
            setMoreMenuAnchor(null);
          }}
          disabled={isCanvasLocked}
        >
          <Error sx={{ mr: 1 }} /> Error Handling
        </MenuItem>
        {hasManagedRunsAccess && (
          <MenuItem
            onClick={() => {
              setManagedRunPolicyOpen(true);
              setMoreMenuAnchor(null);
            }}
            disabled={isCanvasLocked}
          >
            <Security sx={{ mr: 1 }} /> Managed run
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleRenamePlaceholders(true);
            setMoreMenuAnchor(null);
          }}
          disabled={production || isCanvasLocked}
        >
          <FindReplaceIcon sx={{ mr: 1 }} /> Replace Placeholders
        </MenuItem>
      </Menu>
      <WorkflowSchedulesModal
        open={schedulesOpen}
        onClose={() => setSchedulesOpen(false)}
        projectId={aggregate.project_id}
        projectName={aggregate.project_name}
      />
      <ErrorHandlingModal
        open={errorHandlingOpen}
        onClose={() => setErrorHandlingOpen(false)}
        projectId={aggregate.project_id}
        projectName={aggregate.project_name}
      />
      {hasManagedRunsAccess && (
        <ManagedRunPolicyModal
          open={managedRunPolicyOpen}
          onClose={() => setManagedRunPolicyOpen(false)}
          projectId={aggregate.project_id}
          projectName={aggregate.project_name}
        />
      )}
    </Grid>
  );
}
